import { useState, useEffect } from "react";
import { Box, Grid, keyframes, Typography, FormControl, Paper } from "@mui/material";
import { useErrorBoundary } from "react-error-boundary";
import { gateway } from "../gateway";
import CheckExperience from "./checkboxExperienceResidential";
import { CheckExperienceCommercial } from "./checkboxExperienceCommercial";
import { SnackbarAlert } from "./common/SnackbarAlert";
import { LoaderComponent } from "./Loader";
import { ButtonsHoursOperation } from "./ButtonsHoursOperation";

export const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

export const ConnectBusinessSkills = () => {
  const { showBoundary } = useErrorBoundary();
  const tokenLocal = sessionStorage.getItem("token");

  const [myResidencialServices, setMyResidencialServices] = useState([]);
  const [myCommercialServices, setMyCommercialServices] = useState([]);
  const [backupResidencial, setBackupResidencial] = useState([]);
  const [backupCommercial, setBackupCommercial] = useState([]);

  const [open, setOpen] = useState(false);
  const [changes, setChanges] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [triggerShake, setTriggerShake] = useState(false);

  const [selected, setSelected] = useState("Residential");
  const options = ["Residential", "Commercial"];

  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("");

  const getMyServices = async () => {
    try {
      const response = await gateway.get(`/workservices/self`, {
        headers: { Authorization: `${tokenLocal}` },
      });
      const list = response?.data?.data ?? [];

      const commercial = list.filter((s) => s.workTypes?.type === "commercial");
      const residential = list.filter((s) => s.workTypes?.type === "residencial");

      setMyCommercialServices(commercial ?? []);
      setMyResidencialServices(residential ?? []);
      setBackupCommercial(JSON.parse(JSON.stringify(commercial)));
      setBackupResidencial(JSON.parse(JSON.stringify(residential)));
      setChanges(false);
    } catch (error) {
      showBoundary(error);
    }
  };

  useEffect(() => {
    getMyServices();
  }, []);

  const handleTabChange = (option) => {
    if (changes) {
      setAlertMessage("Please save your changes before switching tabs");
      setAlertSeverity("warning");
      setOpenAlert(true);
      setTriggerShake(true);
      setTimeout(() => setTriggerShake(false), 600);
      return;
    }
    setSelected(option);
  };

  const handleToggleService = (serviceId, checked) => {
    setChanges(true);

    const setter = selected === "Residential" ? setMyResidencialServices : setMyCommercialServices;

    setter((prev) => prev.map((s) => (s.id === serviceId ? { ...s, UserWorkTypeServices: { ...s.UserWorkTypeServices, isActive: checked } } : s)));
  };

  const saveMyServices = async () => {
    try {
      const allServices = [...(myResidencialServices ?? []), ...(myCommercialServices ?? [])];
      const servicesPayload = allServices.map((s) => ({
        id: s.id,
        isActive: !!s.UserWorkTypeServices?.isActive,
      }));

      await gateway.put(`/workservices/self`, { services: servicesPayload }, { headers: { Authorization: `${tokenLocal}` } });
      setBackupResidencial(JSON.parse(JSON.stringify(myResidencialServices)));
      setBackupCommercial(JSON.parse(JSON.stringify(myCommercialServices)));
      setChanges(false);
      setAlertMessage("Services Updated");
      setAlertSeverity("success");
    } catch (error) {
      setAlertMessage("Error updating services");
      setAlertSeverity("error");
    } finally {
      setOpenAlert(true);
    }
  };

  const handleServiceUpdate = async () => {
    setOpen(true);
    await saveMyServices();
    setOpen(false);
  };

  useEffect(() => {
    if (cancel) {
      setMyResidencialServices(JSON.parse(JSON.stringify(backupResidencial)));
      setMyCommercialServices(JSON.parse(JSON.stringify(backupCommercial)));
      setCancel(false);
      setChanges(false);
    }
  }, [cancel, backupResidencial, backupCommercial]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "900px",
          borderRadius: "24px",
          border: "1px solid var(--fx-surface-border, #E2E8F0)",
          overflow: "hidden",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            py: 3,
            px: 3,
            alignItems: "center",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              position: "relative",
              border: 1,
              borderColor: "var(--fx-surface-border, #E2E8F0)",
              borderRadius: "10px",
              px: 1,
              width: "100%",
              maxWidth: "4",
              cursor: "pointer",
              backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
              overflow: "hidden",
              mb: 3,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 4,
                left: 4,
                width: "calc(50% - 4px)",
                height: "calc(100% - 8px)",
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                borderRadius: "10px",
                transition: "transform 0.3s ease",
                transform: `translateX(${options.indexOf(selected) * 100}%)`,
                zIndex: 1,
              }}
            />

            {options.map((option) => (
              <Typography
                key={option}
                onClick={() => handleTabChange(option)}
                sx={{
                  fontWeight: 500,
                  fontSize: "1rem",
                  px: 2,
                  py: 1,
                  flex: 1,
                  textAlign: "center",
                  position: "relative",
                  zIndex: 2,
                  color: selected === option ? "#ff9800" : "var(--fx-surface-textsecondary, #64748B)",
                }}
              >
                {option}
              </Typography>
            ))}
          </Box>

          <Box sx={{ width: "100%" }}>
            {selected === "Residential" ? (
              <CheckExperience data={myResidencialServices} onToggle={handleToggleService} setChanges={setChanges} />
            ) : (
              <CheckExperienceCommercial data={myCommercialServices} onToggle={handleToggleService} setChanges={setChanges} />
            )}

            <Box sx={{ mt: 4, display: "flex", justifyContent: "end" }}>
              <ButtonsHoursOperation
                disabled={!changes}
                setCancel={setCancel}
                onSubmit={handleServiceUpdate}
                sx={{ animation: triggerShake ? `${shakeAnimation} 0.5s ease-in-out` : "none" }}
              />
            </Box>
          </Box>
        </Box>

        <LoaderComponent type="card" open={open} hideBackdrop />
        <SnackbarAlert openState={openAlert} setOpenState={setOpenAlert} severity={alertSeverity} duration={4500} message={alertMessage} />
      </Paper>
    </Box>
  );
};
